import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, message } from 'antd';
import HumanPullHero from './components/HumanPullHero';
import PullTrack from './components/PullTrack';
import NextActionBand, { NextAction } from './components/NextActionBand';
import { ContactFace } from './components/ContactCluster';
import {
  getDashboardStats,
  getGmailStatus,
  getGmailAuthUrl,
  getApplications,
} from '../../services/api';
import { DashboardStats } from '../../types';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useNotifications } from '../../contexts/NotificationContext';
import { setGmailReturnTo, clearAutoApplyResume } from '../../utils/gmailReturn';
import { useGmailConnectResult } from '../../hooks/useGmailConnectResult';
import { useGmailConsentPrimer } from '../../hooks/useGmailConsentPrimer';
import { useSearchDrawer } from '../../contexts/SearchDrawerContext';
import { captureEvent } from '../../services/analytics';
import mascot from '../../images/beast-mascot.webp';
import css from './Dashboard.module.scss';

const HERO_KEY = 'dashboardHeroExpanded';

type NextKey = 'gmail' | 'create' | 'review' | 'running' | 'caughtup';
type FocusKey = 'searches' | 'opportunities' | 'process' | null;

// Priority order matches the design's §6 table: gmail → create → review →
// running (searches still working) → caught-up (nothing active).
const resolveNext = (stats: DashboardStats, gmailConnected: boolean | null): NextKey => {
  if ((stats.totalApplications ?? 0) > 0 && gmailConnected === false) return 'gmail';
  if ((stats.totalSearches ?? 0) === 0) return 'create';
  if ((stats.pendingApproval ?? 0) > 0) return 'review';
  if ((stats.activeSearches ?? 0) > 0) return 'running';
  return 'caughtup';
};

const focusFor = (key: NextKey): FocusKey => {
  switch (key) {
    case 'create':
    case 'running':
      return 'searches';
    case 'review':
      return 'opportunities';
    case 'caughtup':
      return 'process';
    default:
      return null;
  }
};

// Short, honest descriptor of contact quality across the surfaced opportunities
// (the playbook's quality ladder). contactsByType counts opportunities, not
// people emailed — so it belongs here, on Opportunities, not the reached node.
const qualityNote = (stats: DashboardStats): string | undefined => {
  const role = stats.contactsByType?.personal_role ?? 0;
  const hr = stats.contactsByType?.personal_hr ?? 0;
  if ((stats.totalOpportunities ?? 0) === 0 || (role === 0 && hr === 0)) return undefined;
  if (role >= hr * 2) return 'mostly hiring managers';
  if (hr >= role * 2) return 'mostly recruiters';
  return 'hiring managers & recruiters';
};

// Warm-channel mix across ALL applications (applicationsByChannel includes
// archived, matching totalApplications). Silent until the user has at least one
// declared app — all-zero users see exactly the pre-referral dashboard. Lives on
// In-process because declared apps never pass through Reached (no email sent).
const channelFootnote = (stats: DashboardStats): string | undefined => {
  const referral = stats.applicationsByChannel?.referral ?? 0;
  const direct = stats.applicationsByChannel?.direct ?? 0;
  if (referral === 0 && direct === 0) return undefined;
  const parts: string[] = [];
  if (referral > 0) parts.push(`${referral} via referral`);
  if (direct > 0) parts.push(`${direct} direct`);
  return parts.join(' · ');
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { subscribe } = useNotifications();
  const { openDrawer } = useSearchDrawer();

  // Show the connect toast here when the user started the connect from the
  // dashboard (the backend lands them on /settings, which relays back).
  useGmailConnectResult();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsError, setStatsError] = useState(false);
  const [gmailConnected, setGmailConnected] = useState<boolean | null>(null);
  const [faces, setFaces] = useState<ContactFace[]>([]);

  const [heroOverride, setHeroOverrideState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(HERO_KEY);
    } catch {
      return null;
    }
  });
  const setHeroOverride = (v: '0' | '1') => {
    try {
      localStorage.setItem(HERO_KEY, v);
    } catch {
      /* private mode — fall back to in-memory only */
    }
    setHeroOverrideState(v);
  };

  const fetchStats = useCallback(async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
      setStatsError(false);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      setStatsError(true);
      message.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGmailStatus = useCallback(async () => {
    try {
      const status = await getGmailStatus();
      setGmailConnected(status.connected);
    } catch {
      // Unknown, not disconnected — a transient error must not trigger a
      // reconnect prompt for an actually-connected user.
      setGmailConnected(null);
    }
  }, []);

  // A handful of recent contacted applications give the "Reached out" node real
  // faces — the counts come from stats, the names don't.
  const fetchFaces = useCallback(async () => {
    try {
      const { applications } = await getApplications({
        limit: 6,
        sort_field: 'submitted_at',
        sort_method: 'desc',
      });
      setFaces(
        applications.map((a) => {
          const fn = a.contact?.firstName ?? '';
          const ln = a.contact?.lastName ?? '';
          const initials = ((fn[0] ?? '') + (ln[0] ?? '')).toUpperCase() || '–';
          const name = `${`${fn} ${ln}`.trim()}${a.company?.name ? ` · ${a.company.name}` : ''}`;
          return { initials, name };
        })
      );
    } catch {
      setFaces([]);
    }
  }, []);

  const loadAll = useCallback(() => {
    fetchStats();
    fetchGmailStatus();
    fetchFaces();
  }, [fetchStats, fetchGmailStatus, fetchFaces]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    return subscribe(
      [
        'search_status_changed',
        'opportunities_ready',
        'application_status_changed',
        'recommended_action_changed', // keeps the reply count live
      ],
      (event) => {
        fetchStats();
        // Avatar faces only change when a new application appears — a reply or a
        // search-status change adds no new face, so don't re-fetch them.
        if (event.notificationType === 'application_status_changed') {
          fetchFaces();
        }
      }
    );
  }, [subscribe, fetchStats, fetchFaces]);

  const selectStation = (station: string) => () =>
    captureEvent('dashboard_station_clicked', { station });

  const handleConnectGmail = async () => {
    try {
      setGmailReturnTo('/'); // come back to the dashboard, not Settings
      // Not an Auto-Apply flow — drop any pending one so this connect cannot
      // spring the Auto-Apply setup modal on arrival.
      clearAutoApplyResume();
      const { authUrl } = await getGmailAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      message.error(getApiErrorMessage(error));
    }
  };

  // Coach the "Send email on your behalf" checkbox before leaving for Google;
  // Continue runs the untouched redirect above.
  const { openPrimer, primer } = useGmailConsentPrimer(handleConnectGmail);

  const handleCreateSearch = () => openDrawer();

  const handleHeroCollapse = () => {
    setHeroOverride('0');
    captureEvent('dashboard_hero_collapsed');
  };
  const handleHeroExpand = () => {
    setHeroOverride('1');
    captureEvent('dashboard_hero_expanded');
  };

  const handleRetry = () => {
    setLoading(true);
    loadAll();
  };

  if (loading) {
    return (
      <div className={css.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  // Distinguish "fetch failed" from "new user with no data" — never show
  // first-run onboarding when we simply couldn't load.
  if (statsError && !stats) {
    return (
      <div className={css.dashboardWrapper}>
        <div className={css.errorCard} role="alert">
          <p className={css.errorText}>
            We couldn't load your dashboard. Check your connection and try again.
          </p>
          <button type="button" className={css.errorRetry} onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const s = stats as DashboardStats;
  const pending = s.pendingApproval ?? 0;
  const inProcess = s.applicationsInProcess ?? 0;
  const contacted = s.totalContacted ?? 0;
  const replied = s.totalReplied ?? 0;

  const nextKey = resolveNext(s, gmailConnected);
  const focus = focusFor(nextKey);

  const buildAction = (key: NextKey): NextAction => {
    const wrap = (fn: () => void) => () => {
      captureEvent('dashboard_next_action_clicked', { state: key });
      fn();
    };
    switch (key) {
      case 'gmail':
        return {
          key,
          copy: 'Your Gmail connection dropped. Reconnect to keep sending from your inbox.',
          buttonLabel: 'Reconnect Gmail',
          onClick: wrap(() => openPrimer('connect')),
          loud: true,
        };
      case 'create':
        return {
          key,
          copy: "Tell us the role you want. We'll find the people to reach.",
          buttonLabel: 'Create a search',
          onClick: wrap(handleCreateSearch),
          loud: true,
        };
      case 'review':
        return {
          key,
          copy: `${pending} ${pending === 1 ? 'opportunity is' : 'opportunities are'} ready. Each one comes with a person to reach.`,
          buttonLabel: 'Review & reach out',
          onClick: wrap(() => navigate('/opportunities')),
          loud: true,
        };
      case 'running':
        return {
          key,
          copy: "Your searches are running. We're finding roles and the people behind them.",
          buttonLabel: 'View searches',
          onClick: wrap(() => navigate('/searches')),
          loud: false,
        };
      case 'caughtup':
      default:
        return {
          key,
          copy:
            inProcess > 0
              ? `You're all caught up. ${inProcess} ${inProcess === 1 ? 'conversation' : 'conversations'} in process.`
              : "You're all caught up.",
          buttonLabel: 'View applications',
          onClick: wrap(() => navigate('/applications')),
          loud: false,
        };
    }
  };

  // Green fill: light up the rail through the furthest stage reached.
  const reachedFlags = [
    (s.totalSearches ?? 0) > 0,
    (s.totalOpportunities ?? 0) > 0,
    contacted > 0,
    replied > 0,
    inProcess > 0,
  ];
  let filledThrough = 0;
  reachedFlags.forEach((r, i) => {
    if (r) filledThrough = i;
  });

  const heroComplete = (s.totalSearches ?? 0) > 0 && (s.totalApplications ?? 0) > 0;
  const heroExpanded = heroOverride !== null ? heroOverride === '1' : !heroComplete;

  return (
    <div className={css.dashboardLayout}>
      <div className={css.dashboardWrapper}>
      <HumanPullHero
        expanded={heroExpanded}
        onCollapse={handleHeroCollapse}
        onExpand={handleHeroExpand}
      />

      <PullTrack
        filledThrough={filledThrough}
        searches={{
          tone: 'origin',
          label: 'Searches',
          value: s.activeSearches ?? 0,
          sublabel: 'running',
          tooltip: 'Searches actively finding roles and the people behind them.',
          focused: focus === 'searches',
          to: '/searches',
          onSelect: selectStation('searches'),
        }}
        opportunities={{
          tone: 'opportunities',
          label: 'Opportunities',
          value: pending,
          sublabel: 'to review',
          footnote: qualityNote(s),
          tooltip: 'Matches waiting for your review. Each comes with a person to reach.',
          focused: focus === 'opportunities',
          to: '/opportunities',
          onSelect: selectStation('opportunities'),
        }}
        reached={{
          value: contacted,
          faces,
          ghost: contacted === 0,
          tooltip: "Applications you've actually emailed (at least one message).",
          to: '/applications',
          onSelect: selectStation('reached'),
        }}
        replies={{
          tone: 'replies',
          label: 'Replies',
          value: replied,
          sublabel: 'replied',
          tooltip: 'Counts replies routed through Job Beast forwarding.',
          to: '/applications',
          onSelect: selectStation('replies'),
        }}
        process={{
          tone: 'process',
          label: 'In process',
          value: inProcess,
          sublabel: 'in conversation',
          footnote: channelFootnote(s),
          tooltip: "Active applications that aren't archived or finished.",
          focused: focus === 'process',
          to: '/applications',
          onSelect: selectStation('process'),
        }}
      />

      <NextActionBand action={buildAction(nextKey)} />
      </div>

      {/* Brand mascot — fills the right-hand white space on wide screens only. */}
      <aside className={css.mascot} aria-hidden="true">
        <img src={mascot} alt="" className={css.mascotImg} />
      </aside>

      {primer}
    </div>
  );
};

export default Dashboard;
